import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import SEO from '../components/SEO/SEO';

const Terms = () => {
  return (
    <>
      <SEO 
        title="Terms of Service - Paisalocal"
        description="Read our comprehensive terms of service. Learn about contractor obligations, payment terms, project agreements, and more on Paisalocal's platform."
        keywords="terms of service, contractor terms, payment terms, project agreements, home improvement terms, construction terms"
      />
      <TermsContainer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <Title>Terms of Service</Title>
        <LastUpdated>Last Updated: January 29, 2024</LastUpdated>

        <Section>
          <SectionTitle>1. Agreement to Terms</SectionTitle>
          <Content>
            By accessing or using Xtreme Roofing & Construction's services, you agree to be bound by these Terms of Service. If you disagree with any part of these terms, you may not access our services.
          </Content>
        </Section>

        <Section>
          <SectionTitle>2. Services</SectionTitle>
          <Content>
            <p>Xtreme Roofing & Construction provides roofing and construction services including but not limited to:</p>
            <List>
              <ListItem>Residential and commercial roofing installation</ListItem>
              <ListItem>Roof repair and maintenance</ListItem>
              <ListItem>Emergency roofing services</ListItem>
              <ListItem>Construction and renovation services</ListItem>
              <ListItem>Inspections and consultations</ListItem>
            </List>
          </Content>
        </Section>

        <Section>
          <SectionTitle>3. Estimates and Pricing</SectionTitle>
          <Content>
            All estimates provided are subject to change based on material costs, project scope modifications, and unforeseen circumstances. Final pricing will be confirmed in writing before work begins.
          </Content>
        </Section>

        <Section>
          <SectionTitle>4. Warranties</SectionTitle>
          <Content>
            Our services come with specific warranties as detailed in your service agreement. Manufacturer warranties are handled according to their respective terms and conditions.
          </Content>
        </Section>

        <Section>
          <SectionTitle>5. Payment Terms</SectionTitle>
          <Content>
            <p>Payment terms are as follows:</p>
            <List>
              <ListItem>Deposit required to secure project start date</ListItem>
              <ListItem>Progress payments may be required for larger projects</ListItem>
              <ListItem>Final payment due upon project completion</ListItem>
              <ListItem>Accepted payment methods will be specified in your contract</ListItem>
            </List>
          </Content>
        </Section>

        <Section>
          <SectionTitle>6. Cancellation Policy</SectionTitle>
          <Content>
            Project cancellations must be submitted in writing. Refund of deposits will be handled according to the stage of the project and costs incurred.
          </Content>
        </Section>

        <Section>
          <SectionTitle>7. Insurance and Licensing</SectionTitle>
          <Content>
            We maintain comprehensive insurance coverage and required licensing. Documentation available upon request.
          </Content>
        </Section>

        <Section>
          <SectionTitle>8. Property Access and Preparation</SectionTitle>
          <Content>
            Clients must provide reasonable access to the work area and ensure the area is prepared according to provided guidelines.
          </Content>
        </Section>

        <Section>
          <SectionTitle>9. Dispute Resolution</SectionTitle>
          <Content>
            Any disputes will be resolved through mediation or arbitration before pursuing legal action, unless immediate legal action is required.
          </Content>
        </Section>

        <ContactInfo>
          <p>For questions about these terms, please contact us:</p>
          <p>Xtreme Roofing & Construction</p>
          <p>Phone: (817) 655-2768</p>
          <p>Email: sales@xtremeroofingandconstruction.net</p>
        </ContactInfo>
      </TermsContainer>
    </>
  );
};

const TermsContainer = styled(motion.div)`
  max-width: 800px;
  margin: 4rem auto;
  padding: 0 2rem;
`;

const Title = styled.h1`
  font-size: clamp(2rem, 4vw, 2.5rem);
  text-align: center;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
`;

const LastUpdated = styled.p`
  text-align: center;
  color: var(--text-light);
  margin-bottom: 3rem;
  font-size: 0.9rem;
`;

const Section = styled.section`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.3rem;
  color: var(--text);
  margin-bottom: 1rem;
`;

const Content = styled.div`
  color: var(--text-light);
  line-height: 1.6;
  
  p {
    margin-bottom: 1rem;
  }
`;

const List = styled.ul`
  list-style: none;
  padding-left: 1.5rem;
  margin: 1rem 0;
`;

const ListItem = styled.li`
  color: var(--text-light);
  margin-bottom: 0.5rem;
  position: relative;

  &:before {
    content: "•";
    color: var(--primary);
    position: absolute;
    left: -1.5rem;
  }
`;

const ContactInfo = styled.div`
  margin-top: 3rem;
  padding: 2rem;
  background: var(--background-light);
  border-radius: var(--radius-lg);
  border: 1px solid var(--border);
  text-align: center;
  color: var(--text-light);

  p {
    margin-bottom: 0.5rem;
  }
`;

export default Terms; 